/* eslint-disable react/default-props-match-prop-types */
/* eslint-disable @typescript-eslint/no-shadow */
import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { MuiPickersUtilsProvider, KeyboardDatePicker as Picker } from '@material-ui/pickers';
import moment from 'moment';
import Calendar from '@material-ui/icons/DateRange';
import MomentUtils from '@date-io/moment';
import { FormInputPropTypes } from 'components/styleguide/fields/types';
import PropTypes from 'prop-types';
import withFormControl from 'components/styleguide/fields/withFormControl';
import InputText from '../InputText';
import useStyles from './styles';
import StaticPicker from '../StaticPicker';

const DatePicker = ({
  clearable,
  disabled,
  error,
  format,
  fullWidth,
  onBlur,
  onChange,
  name,
  placeholder,
  formatDate,
  tabIndex,
  value,
  variant,
  readOnly,
  label,
  popover,
  minDate,
  testId,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (event) => {
    if (!popover || disabled) return;
    // Only the calendar icon opens the picker. This handler is bound to the whole field, and
    // the stopPropagation below keeps focus off the input for good — so with it bound to
    // everything, the field could never be typed into, only picked from. Checking for the
    // input alone is not enough: MUI lays a notched-outline <fieldset> over the field, and a
    // real click often lands on that rather than on the input. [QW-326]
    if (!event.target.closest?.('.MuiInputAdornment-root')) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const closePopover = (value) => {
    setAnchorEl(null);
    if (value) onChange(value);
  };
  if (readOnly) {
    return (
      <Grid container alignItems="center" wrap="nowrap" className={classes.readOnly}>
        <Box mr={0.625} pt={1}>
          <Calendar color="primary" />
        </Box>
        <InputText
          name={name}
          readOnly
          fullWidth={false}
          value={value ? formatDate(value, format) : ''}
          classesOverrides={{
            input: classes.readOnlyInput,
          }}
        />
      </Grid>
    );
  }

  const handleKeyDown = (e) => {
    if (value) return;
    // If !value and user pressed Backspace or Surp, do nothing
    if (e.keyCode === 8 || e.keyCode === 46) e.preventDefault();
  };

  const handleBlur = () => {
    const event = { ...new Event('blur'), target: { value, name } };
    onBlur(event);
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      {popover && (
        <StaticPicker anchorEl={anchorEl} minDate={minDate} onUpdate={closePopover} label={label} value={value} />
      )}
      <Picker
        autoOk
        onKeyDown={handleKeyDown}
        className={classes.datepicker}
        clearable={clearable}
        data-testid={testId}
        disabled={disabled}
        disableToolbar
        emptyLabel={placeholder || format.toUpperCase()}
        format={format}
        invalidDateMessage=""
        minDate={minDate}
        minDateMessage=""
        maxDateMessage=""
        InputProps={{
          fullWidth,
          notched: false,
          variant: 'outlined',
          onBlur,
          inputProps: {
            'aria-describedby': name,
            id: name,
            name,
            tabIndex,
          },
        }}
        InputAdornmentProps={{ position: 'start' }}
        InputLabelProps={{
          /** @see https://material-ui.com/components/text-fields/#shrink */
          shrink: true,
        }}
        onClose={handleBlur}
        inputVariant="outlined"
        keyboardIcon={<Calendar color="primary" />}
        onMouseDown={handleOpen}
        onClick={handleOpen}
        onChange={onChange}
        error={!!error}
        value={value || null}
        variant={variant}
      />
    </MuiPickersUtilsProvider>
  );
};

DatePicker.propTypes = {
  ...FormInputPropTypes,
  // Overrides the shared input type, which admits only a number or a string. Every real
  // caller of this field passes a date: the dates drawer holds a `Date`, FormDatePicker
  // hands over a dayjs object, and `formatDate` is given whichever it is. The shared type
  // is left alone because eight other fields use it, and for a text input a date would be
  // wrong. [QW-323]
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
  clearable: PropTypes.bool,
  format: PropTypes.string.isRequired,
  formatDate: PropTypes.func,
  tabIndex: PropTypes.number,
  // If true, will open the popover static picker onClick
  popover: PropTypes.bool,
  variant: PropTypes.oneOf(['dialog', 'inline', 'static']),
  testId: PropTypes.string,
};

DatePicker.defaultProps = {
  // Moment tokens, not date-fns: the editable input renders through MomentUtils
  // (see MuiPickersUtilsProvider above), where lowercase 'dd' means short day
  // name (Mon, Tue...), not day-of-month — the date-fns casing here rendered
  // every date as e.g. "10/Th/2026". Caught live testing QW-323, the first
  // caller to use this default rather than overriding it (as
  // LicensorsFiltersPanel and its three siblings already do, for exactly this
  // reason).
  format: 'MM/DD/YYYY',
  // Paired with the format above: this is the readOnly branch's formatter
  // (`formatDate(value, format)`), and it must agree with the Picker on what
  // 'DD' vs 'dd' means. The default used to be date-fns's own `format`, which
  // uses the OPPOSITE convention (dd=day-of-month, DD=day-of-year) and hard-
  // throws on 'DD' — a RangeError, not a silent wrong render. Nothing had ever
  // exercised readOnly with a real format override together until QW-323's
  // LetterRow, the first caller anywhere to pass `readOnly` to this field.
  formatDate: (date, format) => moment(date).format(format),
  name: '',
  variant: 'inline',
  testId: 'datepicker-field',
};

export default withFormControl(DatePicker);
